import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import toast from 'react-hot-toast';

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-5 h-5"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const LocationIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path
      fillRule="evenodd"
      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
      clipRule="evenodd"
    />
  </svg>
);

function ApplyModal({ job, onClose }) {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    linkedin: "",
    cover: "",
    experience: "",
    resume: null,
  });

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const newErrors = {};

    // Name
    if (!form.name?.trim()) {
      newErrors.name = "Full name is required";
    }

    // Email
    if (!form.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
      newErrors.email = "Invalid email address";
    }

    // Phone
    if (!form.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9+\-\s()]{8,20}$/.test(form.phone)) {
      newErrors.phone = "Invalid phone number";
    }

    // Experience
    if (!form.experience) {
      newErrors.experience = "Please select experience";
    }

    // Resume
    if (!form.resume) {
      newErrors.resume = "Resume is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!form.cover?.trim()) {
      newErrors.cover = "Cover letter is required";
    } else if (form.cover.trim().length < 30) {
      newErrors.cover = "Cover letter should be at least 30 characters";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    setErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
    }));
  };

  const handleFile = (e) => {
    const file = e.target.files[0];

    if (file) {
      setForm((prev) => ({
        ...prev,
        resume: file,
      }));

      setErrors((prev) => ({
        ...prev,
        resume: "",
      }));
    }
  };

  const nextStep = () => {
    const isValid = validateStep1();

    if (isValid) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    const isValid = validateStep2();

    if (!isValid) return;

    try {
      setLoading(true);

      await emailjs.send(
        "service_xccirpg",
        "template_uzukwk8",
        {
          job_title: job.title,
          applicant_name: form.name,
          applicant_email: form.email,
          applicant_phone: form.phone,
          linkedin: form.linkedin,
          experience: form.experience,
          cover_letter: form.cover,
        },
        "XkYTBlLQFelDc5Avb",
      );

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.93, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 16 }}
          transition={{ duration: 0.32 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#0d1b2e] to-[#1a2d4a] px-8 py-6 shrink-0">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-orange-400 text-xs font-bold tracking-widest uppercase mb-1">
                  Apply Now on
                </p>

                <h3 className="text-white font-extrabold text-xl leading-snug">
                  {job.title}
                </h3>

                <div className="flex items-center gap-3 mt-2">
                  <span className="text-gray-400 text-xs flex items-center gap-1">
                    <LocationIcon />
                    {job.location}
                  </span>

                  <span className="text-gray-400 text-xs">{job.type}</span>

                  <span className="text-green-400 text-xs font-bold">
                    {job.salary}
                  </span>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto no-scrollbar p-8">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center text-center py-8"
              >
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-2">
                  Application Submitted!
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
                  Thank you for applying for <strong>{job.title}</strong>. Our
                  team will review your application and get back to you within 5
                  business days.
                </p>
                <button
                  onClick={onClose}
                  className="mt-8 px-8 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors"
                >
                  Back to Jobs
                </button>
              </motion.div>
            ) : step === 1 ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4 "
                >
                  <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-5">
                    Step 1 of 2 — Personal Details
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-gray-600 mb-1 block">
                        Full Name *
                      </label>

                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-[0.9rem] focus:outline-none focus:border-orange-400 transition-colors ${
                          errors.name ? "border-red-400" : "border-gray-200"
                        }`}
                      />

                      {errors.name && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600 mb-1 block">
                        Email *
                      </label>

                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-[0.9rem] focus:outline-none focus:border-orange-400 transition-colors ${
                          errors.email ? "border-red-400" : "border-gray-200"
                        }`}
                      />

                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600 mb-1 block">
                        Number *
                      </label>

                      <input
                        type="text"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-[0.9rem] focus:outline-none focus:border-orange-400 transition-colors ${
                          errors.phone ? "border-red-400" : "border-gray-200"
                        }`}
                      />

                      {errors.phone && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.phone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600 mb-1 block">
                        Linkedin *
                      </label>

                      <input
                        type="url"
                        name="linkedin"
                        value={form.linkedin}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-[0.9rem] focus:outline-none focus:border-orange-400 transition-colors ${
                          errors.email ? "border-red-400" : "border-gray-200"
                        }`}
                      />

                      {errors.linkedin && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.linkedin}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1 block">
                      Experience *
                    </label>

                    <select
                      name="experience"
                      value={form.experience}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-xl ${
                        errors.experience ? "border-red-400" : "border-gray-200"
                      }`}
                    >
                      <option value="">Select</option>
                      <option>0–2 years</option>
                      <option>3–5 years</option>
                      <option>6–9 years</option>
                      <option>10+ years</option>
                    </select>

                    {errors.experience && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.experience}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1.5 block">
                      Resume / CV *
                    </label>

                    {/* Upload Box */}
                    <label className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-orange-300 transition-colors cursor-pointer block">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFile}
                        className="hidden"
                      />

                      <p className="text-3xl mb-2">📄</p>

                      <p className="text-gray-700 font-semibold text-sm">
                        {form.resume
                          ? form.resume.name
                          : "Drop your resume here"}
                      </p>

                      <p className="text-gray-400 text-xs mt-1">
                        PDF, DOC, DOCX up to 10MB
                      </p>

                      <div className="mt-3 inline-flex px-5 py-2 border border-gray-300 rounded-full text-xs font-bold text-gray-600 hover:border-orange-400 hover:text-orange-500 transition-colors">
                        Browse Files
                      </div>
                    </label>

                    {/* Error */}
                    {errors.resume && (
                      <p className="text-red-500 text-xs mt-2">
                        {errors.resume}
                      </p>
                    )}
                  </div>

                  <motion.button
                    onClick={nextStep}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors"
                  >
                    Continue →
                  </motion.button>
                </motion.div>
              </AnimatePresence>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-5">
                    Step 2 of 2 — Cover Letter
                  </p>
                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1.5 block">
                      Why do you want to join our company? *
                    </label>
                    <textarea
                      name="cover"
                      value={form.cover}
                      onChange={handleChange}
                      rows={6}
                      className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-[0.9rem] focus:outline-none focus:border-orange-400 transition-colors resize-none ${
                        errors.cover ? "border-red-400" : "border-gray-200"
                      }`}
                      placeholder="Tell us about your motivation, relevant experience, and what excites you about this role..."
                    />
                    <p className="text-gray-400 text-xs mt-1.5">
                      {form.cover.length}/1000 characters
                    </p>

                    {errors.cover && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.cover}
                      </p>
                    )}
                  </div>

                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                    <p className="text-xs font-bold text-orange-700 mb-2">
                      📋 Application Summary
                    </p>
                    <div className="space-y-1.5 text-xs text-gray-600">
                      <p>
                        <span className="font-semibold">Role:</span> {job.title}
                      </p>
                      <p>
                        <span className="font-semibold">Name:</span>{" "}
                        {form.name || "—"}
                      </p>
                      <p>
                        <span className="font-semibold">Email:</span>{" "}
                        {form.email || "—"}
                      </p>
                      <p>
                        <span className="font-semibold">Experience:</span>{" "}
                        {form.experience || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(1)}
                      className="flex-1 py-3.5 border-2 border-gray-200 text-gray-700 font-bold rounded-xl hover:border-gray-300 transition-colors"
                    >
                      ← Back
                    </button>
                    <motion.button
                      onClick={handleSubmit}
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors"
                    >
                      {loading ? "Submitting..." : "Submit Application 🚀"}
                    </motion.button>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ApplyModal;
